import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { Alert01Icon } from '@hugeicons/core-free-icons';
import {
  EMAIL_TEMPLATE_BOILERPLATES,
  EMAIL_TEMPLATE_TYPES,
  emailTemplateTypeOptionsForCategory,
} from 'constants/email-config';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Alert from 'components/Alert/Alert';
import { EmailTemplatePreview } from 'components/EmailTemplatePreview/EmailTemplatePreview';
import { FormMessage, MessageType } from 'components/FormComponents';
import {
  Button,
  Input,
  InputGroup,
  MultipleSelect,
  ParamTextarea,
  RichTextEditor,
  Select,
} from 'components/FormComponents';

export const EMAIL_TEMPLATE_CATEGORIES = [
  { value: 'candidate', label: 'Candidate' },
  { value: 'referee', label: 'Referee' },
];

function isBoilerplateUnchanged({ type, subject, body }) {
  if (!type) {
    return false;
  }
  const bp = EMAIL_TEMPLATE_BOILERPLATES[type];
  if (!bp) {
    return false;
  }
  return subject === bp.subject && body === bp.body;
}

function isUnchangedFromAppliedDefault(data, appliedDefaultsRef) {
  const snap = appliedDefaultsRef.current;
  if (snap && snap.type === data.type) {
    return data.subject === snap.subject && data.body === snap.body;
  }
  return isBoilerplateUnchanged(data);
}

export const EmailTemplateForm = forwardRef(
  (
    {
      initialValues,
      onSubmit,
      onCancel,
      isLoading,
      brands = [],
      showSupportFooterInPreview = true,
      hideFooterActions = false,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const isCreate = !initialValues?.id;
    const [boilerplateConfirmOpen, setBoilerplateConfirmOpen] = useState(false);
    const [pendingSubmitPayload, setPendingSubmitPayload] = useState(null);

    const {
      register,
      handleSubmit,
      control,
      setValue,
      getValues,
      watch,
      formState: { errors },
    } = useForm({
      defaultValues: {
        name: '',
        category: 'candidate',
        type: '',
        subject: '',
        body: '',
        brand_ids: [],
        ...initialValues,
      },
    });

    const category = watch('category');
    const selectedType = watch('type');
    const showBoilerplateHint =
      isCreate && selectedType && Boolean(EMAIL_TEMPLATE_BOILERPLATES[selectedType]);

    const subjectEdited = useRef(Boolean(initialValues?.subject));
    const bodyEdited = useRef(Boolean(initialValues?.body));
    const lastBoilerplateRef = useRef(null);
    const settingBoilerplate = useRef(false);
    const appliedDefaultsRef = useRef(null);
    const appliedDefaultsGenerationRef = useRef(0);

    const typeOptions = useMemo(() => emailTemplateTypeOptionsForCategory(category), [category]);

    useEffect(() => {
      const currentType = getValues('type');
      if (!currentType) {
        return;
      }
      const allowed = emailTemplateTypeOptionsForCategory(category);
      if (!allowed.some(o => o.value === currentType)) {
        setValue('type', '');
        appliedDefaultsRef.current = null;
        appliedDefaultsGenerationRef.current += 1;
      }
    }, [category, getValues, setValue]);

    const textParams = useMemo(() => {
      const candidateParams = {
        candidateFirstName: '{{candidateFirstName}}',
        candidateFullName: '{{candidateFullName}}',
        candidateLastName: '{{candidateLastName}}',
        job: '{{job}}',
        company: '{{company}}',
        user: '{{user}}',
      };
      const refereeParams = {
        refereeFirstName: '{{refereeFirstName}}',
        refereeFullName: '{{refereeFullName}}',
        refereeLastName: '{{refereeLastName}}',
        user: '{{user}}',
      };
      return {
        ...candidateParams,
        ...(category === 'referee' || selectedType === EMAIL_TEMPLATE_TYPES.REPLACE_REFEREE
          ? refereeParams
          : {}),
      };
    }, [category, selectedType]);

    useEffect(() => {
      if (initialValues) {
        Object.entries(initialValues).forEach(([key, value]) => setValue(key, value));
      }
    }, [initialValues, setValue]);

    const handleTypeChange = (newType, rhfOnChange) => {
      const boilerplate = EMAIL_TEMPLATE_BOILERPLATES[newType];
      if (boilerplate) {
        const prev = lastBoilerplateRef.current;
        const curSubject = watch('subject');
        const curBody = watch('body');
        const subjectIsPristine =
          !subjectEdited.current || curSubject === '' || curSubject === prev?.subject;
        const bodyIsPristine = !bodyEdited.current || curBody === '' || curBody === prev?.body;
        if (subjectIsPristine) {
          setValue('subject', boilerplate.subject);
          subjectEdited.current = false;
        }
        if (bodyIsPristine) {
          settingBoilerplate.current = true;
          setValue('body', boilerplate.body);
          bodyEdited.current = false;
        }
        lastBoilerplateRef.current = boilerplate;
      }
      rhfOnChange(newType);
      const gen = ++appliedDefaultsGenerationRef.current;
      setTimeout(() => {
        if (gen !== appliedDefaultsGenerationRef.current) {
          return;
        }
        appliedDefaultsRef.current = {
          type: newType,
          subject: getValues('subject'),
          body: getValues('body'),
        };
      }, 0);
    };

    const trySubmit = data => {
      if (isCreate && isUnchangedFromAppliedDefault(data, appliedDefaultsRef)) {
        setPendingSubmitPayload(data);
        setBoilerplateConfirmOpen(true);
        return;
      }
      onSubmit(data);
    };

    const confirmBoilerplateSubmit = () => {
      if (pendingSubmitPayload) {
        onSubmit(pendingSubmitPayload);
      }
      setBoilerplateConfirmOpen(false);
      setPendingSubmitPayload(null);
    };

    const cancelBoilerplateConfirm = () => {
      setBoilerplateConfirmOpen(false);
      setPendingSubmitPayload(null);
    };

    useImperativeHandle(ref, () => ({
      submit: () => handleSubmit(trySubmit)(),
    }));

    return (
      <div className="flex h-full w-full flex-col gap-8 lg:flex-row lg:flex-nowrap lg:items-stretch">
        <Alert
          show={boilerplateConfirmOpen}
          title={t('emailTemplates.boilerplateConfirm.title')}
          message={t('emailTemplates.boilerplateConfirm.message')}
          ok={t('emailTemplates.boilerplateConfirm.ok')}
          icon={Alert01Icon}
          cancel={t('common.cancel')}
          type="warning"
          onOk={confirmBoilerplateSubmit}
          onCancel={cancelBoilerplateConfirm}
        />
        <form
          onSubmit={handleSubmit(trySubmit)}
          className="flex w-full flex-col lg:h-full lg:shrink-0 lg:max-w-5xl lg:basis-1/2 lg:overflow-y-auto">
          <InputGroup title={t('emailTemplates.form.name')} error={errors.name?.message}>
            <Input
              name="name"
              placeholder={t('emailTemplates.form.namePlaceholder')}
              inputProps={register('name', { required: t('emailTemplates.form.nameRequired') })}
              error={errors.name?.message}
            />
          </InputGroup>
          {/* Only UX is changed to audience for better UX. DB and backend are still using category. */}
          <InputGroup
            title={t('emailTemplates.form.audience')}
            description={t('emailTemplates.form.audienceDescription')}>
            <Controller
              name="category"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange } }) => (
                <Select name="category" value={value} onChange={e => onChange(e.target.value)}>
                  {EMAIL_TEMPLATE_CATEGORIES.map(option => (
                    <Select.Item key={option.value} value={option.value}>
                      {option.label}
                    </Select.Item>
                  ))}
                </Select>
              )}
            />
          </InputGroup>
          {/* Only UX is changed to Event for better UX. DB and backend are still using type. */}
          <InputGroup
            title={t('emailTemplates.form.event')}
            description={t('emailTemplates.form.eventDescription')}
            error={errors.type?.message}>
            <Controller
              name="type"
              control={control}
              rules={{ required: t('emailTemplates.form.typeRequired') }}
              render={({ field: { value, onChange } }) => (
                <Select
                  name="type"
                  value={value || ''}
                  onChange={e => handleTypeChange(e.target.value, onChange)}
                  error={errors.type?.message}>
                  <Select.Item value="" disabled>
                    {t('emailTemplates.form.selectType')}
                  </Select.Item>
                  {typeOptions.map(option => (
                    <Select.Item key={option.value} value={option.value}>
                      {t(`emailTemplates.type.${option.value}`, { defaultValue: option.label })}
                    </Select.Item>
                  ))}
                </Select>
              )}
            />
          </InputGroup>
          {brands.length > 0 && (
            <InputGroup
              title={t('emailTemplates.form.brands')}
              description={t('emailTemplates.form.brandsDescription')}>
              <Controller
                name="brand_ids"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <MultipleSelect
                    value={value || []}
                    onChange={onChange}
                    placeholder={t('emailTemplates.form.brandsPlaceholder')}>
                    {brands.map(brand => (
                      <MultipleSelect.Item key={brand.id} value={brand.id}>
                        {brand.name}
                      </MultipleSelect.Item>
                    ))}
                  </MultipleSelect>
                )}
              />
            </InputGroup>
          )}
          {showBoilerplateHint && (
            <FormMessage
              type={MessageType.Info}
              message={t('emailTemplates.form.boilerplateHint')}
              className="mt-0 mb-4"
              closeable={false}
            />
          )}
          <Controller
            control={control}
            name="subject"
            rules={{ required: 'This field is required.' }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <InputGroup title={t('emailTemplates.form.subject')} error={errors.subject?.message}>
                <ParamTextarea
                  value={value || ''}
                  onChange={val => {
                    subjectEdited.current = true;
                    onChange(val);
                  }}
                  textParams={textParams}
                  placeholder="Enter subject..."
                  error={errors.subject?.message}
                  noErrorMessage
                />
              </InputGroup>
            )}
          />
          <InputGroup title={t('emailTemplates.form.body')} error={errors.body?.message} grow>
            <Controller
              name="body"
              control={control}
              rules={{ required: t('emailTemplates.form.bodyRequired') }}
              render={({ field: { value, onChange } }) => (
                <RichTextEditor
                  value={value}
                  onChange={val => {
                    if (settingBoilerplate.current) {
                      settingBoilerplate.current = false;
                    } else {
                      bodyEdited.current = true;
                    }
                    onChange(val);
                  }}
                  error={errors.body?.message}
                  textParams={textParams}
                  storeTextParamsAsMustache
                  grow
                />
              )}
            />
          </InputGroup>
          {!showSupportFooterInPreview && (
            <FormMessage
              type={MessageType.Info}
              message="Support footer will not be included in the email. It is turned off in the brand settings"
              noMargin
              closeable={false}
            />
          )}
          {!hideFooterActions && (
            <div className="mt-4 flex gap-2">
              <Button
                type="primary"
                loading={isLoading}
                onClick={handleSubmit(trySubmit)}
                className="u-width-100">
                {t('emailTemplates.form.save')}
              </Button>
              {onCancel && (
                <Button type="secondary" onClick={onCancel}>
                  {t('common.cancel')}
                </Button>
              )}
            </div>
          )}
        </form>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <EmailTemplatePreview
            className="min-h-[700px] w-full flex-1 lg:min-h-0"
            subject={watch('subject')}
            body={watch('body')}
            showSupportFooter={showSupportFooterInPreview}
            showActions
            textParams={textParams}
          />
        </div>
      </div>
    );
  },
);

EmailTemplateForm.displayName = 'EmailTemplateForm';
